import React, { Suspense, useEffectEvent } from "react";
import { useTranslation } from "react-i18next";
import SectionBackgroundRenderer from "@/components/landingPage/common/SectionBackgroundRenderer";
import { SectionRenderProvider } from "@/components/landingPage/common/SectionRenderContext";
import { LandingUIProvider } from "@/components/landingPage/common/LandingUIContext";
import SectionShell from "@/components/landingPage/common/SectionShell";
import type { SiteConfig } from "@/types/sites";
import { useAnalytics } from "@/analytics";
import { getSectionBg } from "./backgroundUtils";
import { DevSectionBadge } from "./DevSectionBadge";

// Lazy components for code-splitting (always used)
const LazyMap: Record<
  string,
  React.LazyExoticComponent<
    React.ComponentType<{ data: any; config: SiteConfig }>
  >
> = {
  hero: React.lazy(() => import("@/components/landingPage/hero")),
  about: React.lazy(() => import("@/components/landingPage/about")),
  experience: React.lazy(() => import("@/components/landingPage/experience")),
  skills: React.lazy(() => import("@/components/landingPage/skills")),
  projects: React.lazy(() => import("@/components/landingPage/projects")),
  contact: React.lazy(() => import("@/components/landingPage/contact")),
  social: React.lazy(() => import("@/components/landingPage/social")),
  features: React.lazy(() => import("@/components/landingPage/features")),
  testimonials: React.lazy(
    () => import("@/components/landingPage/testimonials")
  ),
  faq: React.lazy(() => import("@/components/landingPage/faq")),
  pricing: React.lazy(() => import("@/components/landingPage/pricing")),
  team: React.lazy(() => import("@/components/landingPage/team")),
  services: React.lazy(() => import("@/components/landingPage/services")),
  gallery: React.lazy(() => import("@/components/landingPage/gallery")),
  timeline: React.lazy(() => import("@/components/landingPage/timeline")),
};

// Moved outside SectionsRenderer to avoid recreating the component type on every render,
// which would cause React to unmount/remount children (including the hero section).
const ReadyNotifier: React.FC<{
  onReady?: () => void;
  firedRef: React.MutableRefObject<boolean>;
  children: React.ReactNode;
}> = ({ onReady, firedRef, children }) => {
  React.useEffect(() => {
    if (onReady && !firedRef.current) {
      firedRef.current = true;
      onReady();
    }
  }, [onReady, firedRef]);
  return <>{children}</>;
};

interface SectionsRendererProps {
  orderedSections: string[];
  content: any;
  config: SiteConfig;
  onFirstSectionReady?: () => void;
  skipSections?: string[];  // Sections to skip (rendered elsewhere, e.g., server-side)
}

const SectionsRenderer: React.FC<SectionsRendererProps> = ({
  orderedSections,
  content,
  config,
  onFirstSectionReady,
  skipSections = [],
}) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  // Skeletons removed intentionally. Use null fallback to avoid visual flashes.

  const firstReadyFiredRef = React.useRef(false);

  // CRITICAL FIX: Pre-compute visible sections with stable references (same fix as LandingPage.tsx)
  const visibleSections = React.useMemo(() => {
    let visibleSectionCount = 0;

    return orderedSections
      .filter((sectionName) => !skipSections.includes(sectionName))  // Skip server-rendered sections
      .map((sectionName) => {
        const Component = LazyMap[sectionName];

        // Check if component exists
        if (!Component) {
          return null;
        }

        const isVisible =
          config.visibility?.[sectionName as keyof typeof config.visibility] !==
            false && content[sectionName];

        if (!isVisible) {
          return null;
        }

        // Pre-compute section data to avoid new object creation in render
        const sectionData = content[sectionName];
        const visibleIndex = visibleSectionCount++;

        return {
          sectionName,
          Component,
          sectionData,
          visibleIndex,
        };
      })
      .filter(Boolean) as Array<{
      sectionName: string;
      Component: React.ComponentType<{ data: any; config: SiteConfig }>;
      sectionData: any;
      visibleIndex: number;
    }>;
  }, [orderedSections, config, content, skipSections]);

  return (
    <>
      {visibleSections.map((sectionInfo) => {
        const { sectionName, Component, sectionData, visibleIndex } =
          sectionInfo;

        // Defer offscreen rendering to improve FCP/LCP and reduce CLS
        const cvProps: React.CSSProperties =
          visibleIndex > 1
            ? ({
                contentVisibility: "auto",
                containIntrinsicSize: "800px",
              } as any)
            : {};

        return (
          <SectionRenderProvider
            key={sectionName}
            value={{
              sectionName,
              sectionIndex: visibleIndex,
              isAlternate: visibleIndex % 2 === 1,
            }}
          >
            <LandingUIProvider configOverride={config}>
              <SectionViewTracker
                sectionKey={sectionName}
                sectionIndex={visibleIndex}
                onView={(key, idx) => track.trackSectionView(key, idx)}
                style={cvProps}
              >
                <SectionBackgroundRenderer
                  config={config}
                  sectionIndex={visibleIndex}
                  sectionName={sectionName}
                >
                  <div className="relative">
                    <DevSectionBadge sectionName={sectionName} config={config} />
                    {sectionData != null ? (
                      <Suspense
                        fallback={
                          visibleIndex === 0 ? (
                          <SectionShell
                            id="hero"
                            className={`flex w-full items-center justify-center ${getSectionBg(config, "hero")}`}
                            ariaLabel={t("landing.hero.ariaLabel", { defaultValue: "Hero Section" })}
                            style={{ minHeight: "100vh" }}
                            contentId="main-content"
                            contentRole="main"
                            wrapperVariant="full-bleed"
                            applySectionPadding={false}
                            contentClassName="relative z-0"
                          >
                            <div className="container mx-auto px-4 sm:px-6 lg:px-8" />
                          </SectionShell>
                          ) : null
                        }
                      >
                        {visibleIndex === 0 && onFirstSectionReady ? (
                          <ReadyNotifier onReady={onFirstSectionReady} firedRef={firstReadyFiredRef}>
                            <Component data={sectionData} config={config} />
                          </ReadyNotifier>
                        ) : (
                          <Component data={sectionData} config={config} />
                        )}
                      </Suspense>
                    ) : null}
                  </div>
                </SectionBackgroundRenderer>
              </SectionViewTracker>
            </LandingUIProvider>
          </SectionRenderProvider>
        );
      })}
    </>
  );
};

export default SectionsRenderer;

function SectionViewTracker(
  {
    sectionKey,
    sectionIndex,
    onView,
    children,
    style,
  }:
  {
    sectionKey: string;
    sectionIndex: number;
    onView: (key: string, idx: number) => void;
    children: React.ReactNode;
    style?: React.CSSProperties;
  }
) {
  const seenRef = React.useRef(false);
  const nodeRef = React.useRef<HTMLDivElement | null>(null);

  // useEffectEvent stabilizes the callback so the IntersectionObserver
  // isn't torn down and recreated when onView reference changes.
  const handleView = useEffectEvent((key: string, idx: number) => {
    onView(key, idx);
  });

  React.useEffect(() => {
    if (!nodeRef.current || seenRef.current) return;
    const node = nodeRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !seenRef.current) {
          seenRef.current = true;
          handleView(sectionKey, sectionIndex);
          io.disconnect();
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [sectionKey, sectionIndex, handleView]);

  return (
    <div ref={nodeRef} style={style}>
      {children}
    </div>
  );
}
